import {tokens} from '@pleo-io/telescope'
import {positionMatchWidth} from '@reach/popover'
import {useRect} from '@reach/rect'
import emojiFlags from 'emoji-flags'
import {
    getCountryCallingCode,
    CountryCode,
    AsYouType,
    parsePhoneNumberFromString,
} from 'libphonenumber-js'
import React, {FC} from 'react'
import styled, {css} from 'styled-components'
import {v4 as uuidv4} from 'uuid'

import useCountryList from '@/hooks/use-country-list'
import Input, {Props as InputProps} from '@helios/shared/components/input'
import {
    StyledListboxPopover,
    StyledListboxButton,
    Wrapper,
    StyledListboxInput,
    StyledListboxOption,
    Arrow,
    ListboxList,
} from '@helios/shared/components/select'
import usePopoverDirection, {PopoverDirection} from '@helios/shared/hooks/use-popover-direction'
import locales from '@helios/shared/locale/locales'
import {InputStylesProps, Label} from '@helios/shared/styles/inputs'
import {Text, text} from '@helios/shared/styles/typography'
import {colors} from '@helios/shared/theme'

interface Props extends Omit<InputProps, 'label' | 'onChange'> {
    setCountryCode: (newValue: CountryCode) => void
    countryCode: CountryCode
    label?: string
    phoneNumber: string
    setPhoneNumber: (phoneNumber: string) => void
}

const PhoneInput: FC<Props> = ({
    setCountryCode,
    countryCode,
    label,
    phoneNumber,
    setPhoneNumber,
    error,
    ...props
}) => {
    const labelId = uuidv4()
    const triggerRef = React.useRef(null)
    const popoverRef = React.useRef(null)
    const wrapperRect = useRect(triggerRef)
    const direction = usePopoverDirection({triggerRef, popoverRef})
    const countryList = useCountryList()

    const countryGroups: Array<typeof countryList> = [[], []]

    countryList.forEach((country) => {
        const isSupportedMarket = locales.some(
            (locale) => locale.country.toUpperCase() === country.code.toUpperCase()
        )

        if (isSupportedMarket) {
            countryGroups[0].push(country)
        } else {
            countryGroups[1].push(country)
        }
    })

    const handlePhoneNumberChange = (value: string) => {
        const formattedNumber = new AsYouType(countryCode).input(value)

        setPhoneNumber(formattedNumber)
    }

    const handlePhoneCountryCodeChange = (countryCode: CountryCode) => {
        const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber, countryCode)

        setCountryCode(countryCode)

        if (parsedPhoneNumber) {
            setPhoneNumber(parsedPhoneNumber.formatNational())
        }
    }

    return (
        <Wrapper space={4} stretch>
            {label && <Label id={labelId}>{label}</Label>}

            <StyledListboxInput
                aria-labelledby={labelId}
                value={countryCode}
                onChange={handlePhoneCountryCodeChange}
            >
                <ListboxButtonWrapper ref={triggerRef}>
                    <CountrySelectButton $direction={direction} $hasError={!!error}>
                        {(props) => (
                            <>
                                {!!props.value && (
                                    <Flag
                                        role="img"
                                        aria-label={emojiFlags.countryCode(props.value).name}
                                    >
                                        {emojiFlags.countryCode(props.value).emoji}
                                    </Flag>
                                )}

                                <Arrow $isExpanded={props.isExpanded} />
                            </>
                        )}
                    </CountrySelectButton>

                    <TextInput
                        type="tel"
                        $hasError={!!error}
                        $direction={direction}
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        {...props}
                    />
                </ListboxButtonWrapper>

                {error && (
                    <Text size="xxs" font="secondary" color="error">
                        {error}
                    </Text>
                )}

                <StyledListboxPopover
                    ref={popoverRef}
                    $direction={direction}
                    position={(targetRect, popoverRect) => {
                        const position = positionMatchWidth(targetRect, popoverRect)

                        return {
                            ...position,
                            width: wrapperRect?.width ?? position.width,
                        }
                    }}
                >
                    <ListboxList>
                        {countryGroups.map((group, i) => (
                            <React.Fragment key={i}>
                                {group.map((country) => (
                                    <CountryOption key={country.code} value={country.code}>
                                        <Flag role="img" aria-label={country.name}>
                                            {emojiFlags.countryCode(country.code).emoji}
                                        </Flag>

                                        <OptionCountryName>{country.name}</OptionCountryName>

                                        <CallingCode>
                                            +{getCountryCallingCode(country.code)}
                                        </CallingCode>
                                    </CountryOption>
                                ))}

                                <hr />
                            </React.Fragment>
                        ))}
                    </ListboxList>
                </StyledListboxPopover>
            </StyledListboxInput>
        </Wrapper>
    )
}

const ListboxButtonWrapper = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
`

interface TextInputProps extends InputStylesProps {
    $direction: PopoverDirection
}

const TextInput = styled(Input)<TextInputProps>(({$direction, $hasError}) => {
    const errorStyles = css`
        &,
        :focus {
            border-color: ${colors.border.error};
            border-left: none;
        }

        [data-state='expanded'] & {
            border-bottom: none;
        }
    `

    const popoverAboveStyles = css`
        border-top-right-radius: 0;
    `

    const popoverBelowStyles = css`
        border-bottom-right-radius: 0;
    `

    return css`
        /**
         * For some reason, after building the project, the order of styles changed and
         * these styles where no longer taking precedence and showing up. It seemed like
         * the TextInput styles (which we extend here) were overriding these.
         */
        && {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;

            [data-state='expanded'] & {
                ${$direction === 'above' && popoverAboveStyles}
                ${$direction === 'below' && popoverBelowStyles}
            }

            ${$hasError && errorStyles}
        }
    `
})

const CountrySelectButton = styled(StyledListboxButton)(({$hasError}) => {
    const errorStyles = css`
        &,
        [data-state='expanded'] & {
            border-color: ${colors.border.error};
            border-right: none;
        }

        [data-state='expanded'] & {
            border-bottom: none;
        }
    `

    return css`
        display: grid;
        column-gap: ${tokens.spacing6};
        grid-template-columns: auto auto;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;

        ${$hasError && errorStyles}
    `
})

const CountryOption = styled(StyledListboxOption)`
    display: grid;
    grid-template-columns: auto 1fr auto;
    column-gap: ${tokens.spacing8};
`

const CallingCode = styled.span`
    color: ${colors.text.lightMuted};
`

const OptionCountryName = styled.span`
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: initial;
    white-space: nowrap;
`

const Flag = styled.span`
    ${text({size: 'sm'})}
    display: flex;
    line-height: 0;
    align-items: center;
`

export default PhoneInput
